import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "card-global" }
const _hoisted_2 = { class: "flex" }
const _hoisted_3 = { class: "page-title" }
const _hoisted_4 = { class: "filter-container" }
const _hoisted_5 = { class: "grid grid-cols-12 gap-6" }
const _hoisted_6 = { class: "col-span-4 md:col-span-4 sm:col-span-4" }
const _hoisted_7 = {
  for: "document-template-name",
  class: "block text-sm font-medium text-gray-700"
}
const _hoisted_8 = { class: "mt-10 flex justify-between" }
const _hoisted_9 = { class: "relative" }
const _hoisted_10 = { class: "flex flex-row-reverse" }

import { ref, onMounted, computed, reactive } from "vue";
import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/vue/solid";
import { useToast } from "vue-toastification";
import DeactivateEntityModal from "@/components/common/DeactivateEntityModal.vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { DocumentTemplateActions } from "@/store/modules/documentTemplate/actions";
import { DocumentTemplateGetters } from "@/store/modules/documentTemplate/getters";
import {
  DotsHorizontalIcon,
  PlusIcon,
  SearchIcon,
} from "@heroicons/vue/outline";
import { ActionMenu } from "@/components/common";
import { Button } from "@/components/common";
import VueTableLite from "vue3-table-lite/ts";
import StatusLabel from "@/components/common/StatusLabel.vue";
import TablePagination from "@/components/common/pagination/TablePagination.vue";
import { t } from "@/i18n";
import DocumentTemplateDataService from "@/services/documents/documentTemplate/DocumentTemplateDataService";
import FileDataService from "@/services/filemanager/files/FileDataService";
import { downloadPdf } from "@/utils/downloadPdf";
import { AuthGetters } from "@/store/modules/auth";
import { formatDateTime } from "@/utils/date";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const store = useStore();
const router = useRouter();
const toast = useToast();

const deactivateModalShow = ref(false);
const deactivateTargetId = ref<string | null>(null);

const dataProvider = computed(() => {
  return store.getters[DocumentTemplateGetters.GET_DATA_PROVIDER];
});
const documentTemplates = computed(() => {
  return dataProvider.value.items;
});
const currentUser = computed(
  () => store.getters[AuthGetters.GET_USER_IDENTITY]
);
const permissions = computed<string[]>(() =>
  Array.isArray(currentUser.value?.permissions)
    ? currentUser.value.permissions
    : []
);
const canCreateDocumentTemplate = computed(() =>
  permissions.value.includes("DOCUMENT_TEMPLATE_CREATE")
);
const canEditDocumentTemplate = computed(() =>
  permissions.value.includes("DOCUMENT_TEMPLATE_EDIT")
);
const canDeactivateDocumentTemplate = computed(() =>
  permissions.value.includes("DOCUMENT_TEMPLATE_DEACTIVATE")
);
const pager = computed(() => {
  return dataProvider.value.pager;
});

const table = reactive({
  isLoading: false,
  columns: [
    {
      label: t("common.title"),
      field: "name",
      sortable: false,
    },
    {
      label: t("common.status"),
      field: "status",
      sortable: false,
    },
    {
      label: t("common.updatedAt"),
      field: "updatedAt",
      sortable: false,
    },
    {
      label: "",
      field: "action",
      sortable: false,
    },
  ],
  rows: documentTemplates,
  totalRecordCount: 0,
});

const dropDownActions = computed(() => {
  const actions = [
    {
      text: t("common.details"),
      icon: EyeIcon,
      iconColor: "text-slate-500",
      action: (id: string) => {
        router.push({ name: "DocumentTemplateDetails", params: { id } });
      },
    },
    {
      text: "PDF",
      icon: DotsHorizontalIcon,
      iconColor: "text-blue-500",
      action: (id: string) => {
        previewPdf(id);
      },
    },
  ];

  if (canEditDocumentTemplate.value) {
    actions.push({
      text: t("common.edit"),
      icon: PencilIcon,
      iconColor: "text-green-500",
      action: (id: string) => {
        router.push({ name: "DocumentTemplateEdit", params: { id: id } });
      },
    });
  }

  if (canDeactivateDocumentTemplate.value) {
    actions.push({
      text: t("userDetails.delete"),
      icon: TrashIcon,
      iconColor: "text-red-500",
      action: (id: string) => {
        deactivateTargetId.value = id;
        deactivateModalShow.value = true;
      },
    });
  }

  return actions;
});

const confirmDeactivate = async (coordinatorId?: string) => {
  if (deactivateTargetId.value == null) {
    return;
  }
  try {
    await store.dispatch(DocumentTemplateActions.DELETE, {
      id: deactivateTargetId.value,
      coordinatorId,
    });
    toast.success(t("approval.submitted"));
    deactivateModalShow.value = false;
    await store.dispatch(DocumentTemplateActions.FETCH_DATA_PROVIDER, {
      page: pager.value.currentPage,
      ...filter.value,
    });
  } catch {
    toast.error(t("deactivate.error"));
  }
};

const previewPdf = async (id: string) => {
  try {
    const templateResponse = await DocumentTemplateDataService.get(id);
    const template = templateResponse.data.data;
    const convertResponse = await DocumentTemplateDataService.convert({
      name: template.name || `template-${id}`,
      templateId: template.id,
      template:
        "<div style=\"font-family: 'times new roman', times, serif;\">" +
        (template.template || "") +
        "</div>",
      footerEnabled: template.footerEnabled ?? false,
      footerScope: template.footerScope ?? "LAST_PAGE",
      footerHtml: template.footerHtml ?? "",
      footerHeightMm: template.footerHeightMm ?? 25,
      fieldConfig: [],
    });
    const fileGuid = convertResponse.data.data.fileGuid;
    const fileResponse = await FileDataService.downloadBlob(fileGuid);
    downloadPdf(fileResponse.data, template.name || `template-${id}`);
  } catch (error) {
    console.error(error);
    toast.error("Не удалось сформировать PDF");
  }
};

const getPage = async (page: number) => {
  await store.dispatch(DocumentTemplateActions.FETCH_DATA_PROVIDER, {
    page: page,
  });
};

// Filter
const filter = ref({
  name: "",
});
const updateFilter = async () => {
  try {
    await store.dispatch(
      DocumentTemplateActions.FETCH_DATA_PROVIDER,
      filter.value
    );
  } catch (e) {
    console.log(e);
  }
};

onMounted(async () => {
  await store.dispatch(DocumentTemplateActions.FETCH_DATA_PROVIDER, {
    page: 0,
  });
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(t)("docTemplateList.title")), 1)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("label", _hoisted_7, _toDisplayString(_unref(t)("common.name")), 1),
            _withDirectives(_createElementVNode("input", {
              id: "document-template-name",
              type: "search",
              class: "w-full input-form",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filter.value.name) = $event))
            }, null, 512), [
              [_vModelText, filter.value.name]
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_unref(Button), {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (updateFilter())),
            class: "btn-search"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(SearchIcon), { class: "button-icon mr-2" }),
              _createTextVNode(" " + _toDisplayString(_unref(t)("common.search")), 1)
            ]),
            _: 1
          }),
          (_unref(canCreateDocumentTemplate))
            ? (_openBlock(), _createBlock(_component_router_link, {
                key: 0,
                to: { name: 'DocumentTemplateCreate' }
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Button), { class: "flex px-10" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(PlusIcon), { class: "button-icon mr-2" }),
                      _createTextVNode(" " + _toDisplayString(_unref(t)("common.create")), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _createVNode(_unref(VueTableLite), {
        "data-pagination-scroll-anchor": "",
        "is-slot-mode": true,
        "is-loading": _unref(table).isLoading,
        columns: _unref(table).columns,
        rows: _unref(table).rows,
        total: _unref(table).totalRecordCount,
        sortable: false,
        "is-hide-paging": true,
        messages: { noDataAvailable: _unref(t)('common.noData') }
      }, {
        status: _withCtx((data) => [
          _createVNode(StatusLabel, {
            label: data.value.status
          }, null, 8, ["label"])
        ]),
        updatedAt: _withCtx((data) => [
          _createTextVNode(_toDisplayString(_unref(formatDateTime)(data.value.updatedAt)), 1)
        ]),
        action: _withCtx((data) => [
          _createElementVNode("div", _hoisted_9, [
            _createVNode(_unref(ActionMenu), {
              actions: _unref(dropDownActions),
              item: data.value
            }, null, 8, ["actions", "item"])
          ])
        ]),
        _: 1
      }, 8, ["is-loading", "columns", "rows", "total", "messages"]),
      _createElementVNode("div", _hoisted_10, [
        _createVNode(TablePagination, {
          current: _unref(pager).currentPage,
          "onUpdate:current": _cache[2] || (_cache[2] = ($event: any) => ((_unref(pager).currentPage) = $event)),
          "total-pages": _unref(pager).totalPages,
          "onUpdate:total-pages": _cache[3] || (_cache[3] = ($event: any) => ((_unref(pager).totalPages) = $event)),
          "start-page": _unref(pager).startPage,
          "end-page": _unref(pager).endPage,
          onPageChanged: getPage
        }, null, 8, ["current", "total-pages", "start-page", "end-page"])
      ])
    ]),
    _createVNode(DeactivateEntityModal, {
      show: deactivateModalShow.value,
      onHide: _cache[4] || (_cache[4] = ($event: any) => (deactivateModalShow.value = false)),
      onConfirm: confirmDeactivate
    }, null, 8, ["show"])
  ], 64))
}
}

})